import { motion } from 'framer-motion'
import { Chart, Hashtag, Monitor, People } from 'iconsax-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import FacebookIcon from '@/assets/socialMedia/Facebook.svg?react'
import InstagramIcon from '@/assets/socialMedia/Instagram.svg?react'
import LinkedInIcon from '@/assets/socialMedia/LinkedIn.svg?react'
import PinterestIcon from '@/assets/socialMedia/Pinterest.svg?react'
import RedditIcon from '@/assets/socialMedia/Reddit.svg?react'
import TikTokIcon from '@/assets/socialMedia/TikTok.svg?react'
import WebIcon from '@/assets/socialMedia/Web.svg?react'
import XIcon from '@/assets/socialMedia/X.svg?react'
import {
  EFindingKind,
  EPatternType,
  ERelationshipTone,
  type IFindingItem,
} from '@/hooks/useReportData'

const PLATFORM_ICONS: Record<string, any> = {
  Facebook: FacebookIcon,
  Instagram: InstagramIcon,
  LinkedIn: LinkedInIcon,
  Pinterest: PinterestIcon,
  Reddit: RedditIcon,
  TikTok: TikTokIcon,
  Twitter: XIcon,
  X: XIcon,
  WebSearch: WebIcon,
}

function formatTime(dateStr?: string): string {
  if (!dateStr) return ''
  try {
    const d = new Date(dateStr)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return ''
  try {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return ''
  }
}

const CARD_MOTION = {
  initial: { opacity: 0, y: 32, scale: 0.94 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -16, scale: 0.97 },
  transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] as const },
  className: 'rounded-2xl overflow-hidden bg-baseColorHighlight p-3 space-y-4',
}

interface FindingItemProps {
  item: IFindingItem
}

export function FindingItem({ item }: FindingItemProps) {
  if (item.kind === EFindingKind.FACE_MATCH)
    return <FaceMatchCard item={item} />
  if (item.kind === EFindingKind.FOOTPRINT) return <FootprintCard item={item} />
  if (item.kind === EFindingKind.PATTERN) return <PatternCard item={item} />
  if (item.kind === EFindingKind.RELATIONSHIP)
    return <RelationshipCard item={item} />
  return <PostCard item={item} />
}

function absoluteUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url
  return `https://${url.replace(/^\/\//, '')}`
}

function isLikelyUsableImage(url?: string): url is string {
  if (!url) return false
  if (url.startsWith('undefined')) return false
  if (url === 'null') return false
  return true
}

function FindingImage({
  url,
  alt = '',
  className,
  onError,
}: {
  url?: string
  alt?: string
  className?: string
  onError?: () => void
}) {
  const [errored, setErrored] = useState(false)
  if (!isLikelyUsableImage(url) || errored) return null
  return (
    <div
      className={
        className ??
        'w-full aspect-[4/3] overflow-hidden bg-neutralColor30/10 rounded-2xl'
      }
    >
      <img
        src={url}
        alt={alt}
        className="w-full h-full object-cover"
        onError={() => {
          setErrored(true)
          onError?.()
        }}
      />
    </div>
  )
}

function Linkable({
  url,
  children,
}: {
  url?: string
  children: React.ReactNode
}) {
  if (!url) return <>{children}</>
  return (
    <a
      href={absoluteUrl(url)}
      target="_blank"
      rel="noopener noreferrer"
      className="block hover:ring-1 hover:ring-neutralColor30/50 rounded-xl transition-all"
    >
      {children}
    </a>
  )
}

function NoImageFallback() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-6 bg-neutralColor30/10 rounded-xl">
      <WebIcon className="w-8 h-8 text-neutralColor30" />
      <span className="text-xs text-neutralColor60">
        {t('dashboard.newSearch.generatingReport.findings.readArticle')}
      </span>
    </div>
  )
}

function FaceMatchCard({ item }: { item: IFindingItem }) {
  const { t } = useTranslation()
  return (
    <Linkable url={item.url}>
      <motion.div {...CARD_MOTION}>
        <FindingImage
          url={item.imageUrl}
          alt="face match"
          className="w-full aspect-[4/3] overflow-hidden bg-baseColorHighlight rounded-2xl"
        />
        <div className="space-y-0.5">
          <p className="text-sm text-neutralColor">
            {t('dashboard.newSearch.generatingReport.findings.faceMatch')}
          </p>
          {item.url && (
            <p className="text-sm text-neutralColor60 truncate">{item.url}</p>
          )}
        </div>
      </motion.div>
    </Linkable>
  )
}

function PostCard({ item }: { item: IFindingItem }) {
  const { t } = useTranslation()
  const Icon = (item.platform && PLATFORM_ICONS[item.platform]) || null
  const time = formatTime(item.date || item.timestamp)
  const date = formatDate(item.date || item.timestamp)

  const headerLabel =
    item.kind === EFindingKind.NEWS
      ? item.source
      : item.userName
        ? `@${item.userName}`
        : item.platform
  const typeLabel =
    item.kind === EFindingKind.NEWS
      ? null
      : item.type ||
        t('dashboard.newSearch.generatingReport.findings.defaultPostType')

  const [imageErrored, setImageErrored] = useState(false)
  const hasImage = isLikelyUsableImage(item.imageUrl) && !imageErrored
  const showFallback = !hasImage && !item.content

  return (
    <Linkable url={item.url}>
      <motion.div {...CARD_MOTION}>
        <div className="flex items-center gap-2 pb-2 border-b border-neutralColor5">
          {Icon && (
            <Icon
              width={12}
              height={12}
              className="text-neutralColor flex-shrink-0"
            />
          )}
          <span className="text-sm text-neutralColor truncate flex-1">
            {headerLabel}
            {typeLabel && (
              <span className="text-neutralColor60"> · {typeLabel}</span>
            )}
            {time && <span className="text-neutralColor60"> · {time}</span>}
            {date && <span className="text-neutralColor60"> · {date}</span>}
          </span>
        </div>

        <FindingImage
          url={item.imageUrl}
          onError={() => setImageErrored(true)}
        />

        {item.content && (
          <p className="text-sm text-neutralColor60 leading-relaxed line-clamp-4">
            {item.content}
          </p>
        )}

        {showFallback && <NoImageFallback />}

        {item.flags && item.flags.length > 0 && (
          <span className="block text-[10px] text-dynamicRed">
            {item.flags.join(', ')}
          </span>
        )}
      </motion.div>
    </Linkable>
  )
}

const PATTERN_TYPE_ICONS: Record<EPatternType, React.ElementType> = {
  [EPatternType.BEHAVIORAL]: Chart,
  [EPatternType.THEMATIC]: Hashtag,
  [EPatternType.PLATFORM_SPECIFIC]: Monitor,
}

function PatternCard({ item }: { item: IFindingItem }) {
  const { t } = useTranslation()
  const Icon = item.patternType ? PATTERN_TYPE_ICONS[item.patternType] : Chart
  const typeLabel = item.patternType
    ? t(
        `dashboard.newSearch.generatingReport.patterns.types.${item.patternType}`,
      )
    : t(
        `dashboard.newSearch.generatingReport.patterns.types.${EPatternType.BEHAVIORAL}`,
      )

  return (
    <motion.div {...CARD_MOTION}>
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <Icon size={12} className="text-neutralColor flex-shrink-0" />
            <span className="text-sm font-semibold tracking-wider uppercase text-neutralColor60 truncate">
              {t('dashboard.newSearch.generatingReport.patterns.label')} ·{' '}
              {typeLabel}
            </span>
          </div>
          {typeof item.confidence === 'number' && (
            <span className="text-sm text-neutralColor60/70 flex-shrink-0 tabular-nums">
              {item.confidence}%
            </span>
          )}
        </div>
        {item.content && (
          <p className="text-sm text-neutralColor60 leading-relaxed">
            {item.content}
          </p>
        )}
      </div>
    </motion.div>
  )
}

const RELATIONSHIP_TONE_COLORS: Record<ERelationshipTone, string> = {
  [ERelationshipTone.FRIENDLY]: 'text-dynamicGreen',
  [ERelationshipTone.COLLABORATIVE]: 'text-dynamicGreen',
  [ERelationshipTone.PROFESSIONAL]: 'text-neutralColor60',
  [ERelationshipTone.AMBIVALENT]: 'text-dynamicYellow',
  [ERelationshipTone.ANTAGONISTIC]: 'text-dynamicRed',
}

function RelationshipCard({ item }: { item: IFindingItem }) {
  const { t } = useTranslation()
  const tone = item.relationshipTone ?? ERelationshipTone.PROFESSIONAL
  const toneColor = RELATIONSHIP_TONE_COLORS[tone]
  const toneLabel = t(
    `dashboard.newSearch.generatingReport.relationships.tones.${tone}`,
  )

  return (
    <motion.div {...CARD_MOTION}>
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <People size={12} className="text-neutralColor flex-shrink-0" />
            <span className="text-sm font-semibold tracking-wider uppercase text-neutralColor60 truncate">
              {t('dashboard.newSearch.generatingReport.relationships.label')}
              {' · '}
              <span className={toneColor}>{toneLabel}</span>
            </span>
          </div>
          {typeof item.confidence === 'number' && (
            <span className="text-sm text-neutralColor60/70 flex-shrink-0 tabular-nums">
              {item.confidence}%
            </span>
          )}
        </div>
        {item.person && (
          <p className="text-sm text-neutralColor font-medium truncate">
            {item.person}
            {item.handles && item.handles.length > 0 && (
              <span className="text-neutralColor60 font-normal">
                {' '}
                · {item.handles.join(', ')}
              </span>
            )}
          </p>
        )}
        {item.content && (
          <p className="text-sm text-neutralColor60 leading-relaxed">
            {item.content}
          </p>
        )}
      </div>
    </motion.div>
  )
}

function FootprintCard({ item }: { item: IFindingItem }) {
  const { t } = useTranslation()
  const Icon = (item.platform && PLATFORM_ICONS[item.platform]) || WebIcon
  const [imageErrored, setImageErrored] = useState(false)
  const hasImage = isLikelyUsableImage(item.imageUrl) && !imageErrored
  const showFallback = !hasImage && !item.content && !item.url

  return (
    <Linkable url={item.url}>
      <motion.div {...CARD_MOTION}>
        <FindingImage
          url={item.imageUrl}
          onError={() => setImageErrored(true)}
        />

        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Icon
              width={20}
              height={20}
              className="text-neutralColor flex-shrink-0"
            />
            <span className="text-sm text-neutralColor truncate">
              {item.platform ||
                t('dashboard.newSearch.generatingReport.findings.web')}
              {item.userName && (
                <span className="text-neutralColor60"> · {item.userName}</span>
              )}
            </span>
          </div>

          {item.content && (
            <p className="text-sm text-neutralColor60 leading-relaxed line-clamp-3">
              {item.content}
            </p>
          )}

          {item.url && !item.content && (
            <p className="text-sm text-neutralColor60 truncate">{item.url}</p>
          )}
        </div>

        {showFallback && <NoImageFallback />}
      </motion.div>
    </Linkable>
  )
}
